import { useCallback, useState } from 'react';

import { cardFramingVariant } from '../util/cardFraming';

/**
 * 🔴 Story 12.15 (AC-5c) — ONE DECODED MAP PER SURFACE, NEVER A HOOK PER CARD.
 *
 * A grid renders its cards inside a `.map()`, so a hook per card is a
 * rules-of-hooks violation the moment the result count changes — and on a
 * 24-card search page it would also mean 24 independent subscriptions to the
 * same three facts. The surface owns ONE state object; each card reports its own
 * decode into it through a single functional `setState`.
 *
 * Nothing here FETCHES anything. The decode rides the request the card's
 * lazy-loader already decided to make, read off that `<img>`'s `onLoad` — see
 * `setDecodedImageSizeFromElement` in `util/useDecodedImageSize.js` for why the
 * off-DOM probe the wizard tiles use is the wrong mechanism on a card grid.
 *
 * ⚠️ Batching: image `load` callbacks land in separate tasks, so React's
 * automatic batching does not merge them. Keeping the map in ONE state object
 * with one updater is what stops that becoming a re-render per card per field.
 * The identity guard below drops repeat reports outright, so a re-decode of an
 * already-known photo re-renders nothing at all.
 *
 * The variant preference is NOT re-derived here — `cardFramingVariant` is the
 * one place that decides what a card frames from, and eight containers calling
 * it is the point of extracting it.
 *
 * @returns {{decodedSizeFor: (listing: Object) => ({width, height}|null),
 *            onImageDecoded: (url: string, size: {width, height}) => void}}
 */
export const useCardDecodedSizes = () => {
  const [sizes, setSizes] = useState({});

  const onImageDecoded = useCallback((url, size) => {
    if (!url || !size) {
      return;
    }
    setSizes(previous => {
      const current = previous[url];
      if (current && current.width === size.width && current.height === size.height) {
        // Same answer as last time — return the SAME object so React bails out.
        return previous;
      }
      return { ...previous, [url]: size };
    });
  }, []);

  const decodedSizeFor = useCallback(
    listing => {
      const url = cardFramingVariant(listing?.images?.[0])?.url;
      return (url && sizes[url]) || null;
    },
    [sizes]
  );

  return { decodedSizeFor, onImageDecoded };
};

export default useCardDecodedSizes;
